// „📋 Liste einfügen“: mehrere Positionen auf einmal erfassen (Liste einfügen → Vorschau → alle anlegen).
import { useState } from 'react';
import { api, type EinkaufSonstigesKategorie, type Lieferant } from '../lib/api';
import { parseListe, wunschKommentar, type ListenZeile } from '../lib/sonstigesListe';
import { ZEUGNIS_HINWEIS } from './SonstigesAktionen';

const BEISPIEL = '15 Spulen D0,8 SG3 von Novofil\n10 Spulen MAG 1.4462 D1,0 von Dratec\n20 Kg 1.4462 D2,4 WIG – Hersteller egal, bitte das günstigste nehmen';

export function ListeEinfuegenModal({ kategorie, lieferanten, onClose, onFertig }: {
  kategorie: EinkaufSonstigesKategorie; lieferanten: Lieferant[]; onClose: () => void; onFertig: () => void;
}) {
  const [text, setText] = useState('');
  const [zeilen, setZeilen] = useState<ListenZeile[] | null>(null);
  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10));
  const [referenz, setReferenz] = useState('');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');

  const set = (i: number, patch: Partial<ListenZeile>) => setZeilen(l => (l ?? []).map((z, k) => (k === i ? { ...z, ...patch } : z)));
  const gueltig = (zeilen ?? []).filter(z => z.was.trim());
  const lieferantenAnzahl = new Set(gueltig.map(z => z.lieferant.trim().toLowerCase()).filter(Boolean)).size;

  async function anlegen() {
    if (gueltig.length === 0) return;
    setBusy(true); setFehler('');
    const offen: ListenZeile[] = [];
    let ok = 0;
    for (const z of gueltig) {
      try {
        await api.einkaufSonstiges.create({
          kategorie, datum, stueck: z.stueck.trim() || null, was: z.was.trim(),
          kommentar: wunschKommentar(z.lieferant), referenz: referenz.trim() || null,
        });
        ok++;
      } catch { offen.push(z); }
    }
    setBusy(false);
    if (ok > 0) onFertig();
    if (offen.length === 0) { onClose(); return; }
    setZeilen(offen);          // nur die fehlgeschlagenen bleiben stehen → erneut versuchen, ohne Doppelte
    setFehler(`${ok} Einträge angelegt, ${offen.length} fehlgeschlagen — unten erneut versuchen.`);
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={busy ? undefined : onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl my-8" data-testid="liste-modal" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">📋 Liste einfügen — mehrere Positionen auf einmal</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none" aria-label="Schließen">×</button>
        </div>
        {zeilen === null ? (
          <>
            <div className="p-6 space-y-3">
              <p className="text-sm text-slate-600">Eine Position pro Zeile einfügen (z. B. aus Teams/Excel). „von …“ am Zeilenende wird als Wunschlieferant erkannt.</p>
              <textarea className="input w-full font-mono text-[13px]" rows={10} aria-label="Liste" placeholder={BEISPIEL} value={text} onChange={e => setText(e.target.value)} />
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
              <button data-testid="liste-weiter" disabled={!text.trim()} onClick={() => setZeilen(parseListe(text))}
                      className="bg-brand text-white rounded-lg px-5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-40">Weiter → Vorschau</button>
            </div>
          </>
        ) : (
          <>
            <div className="p-6 space-y-4">
              <datalist id="sl-lieferanten">{lieferanten.map(l => <option key={l.id} value={l.name} />)}</datalist>
              <div className="flex gap-3 flex-wrap">
                <label className="text-xs text-slate-500">Datum
                  <input type="date" className="input ml-2" aria-label="Datum" value={datum} onChange={e => setDatum(e.target.value)} /></label>
                {kategorie === 'schweissen' && (
                  <label className="text-xs text-slate-500">Referenz (optional, intern)
                    <input className="input ml-2 w-48" aria-label="Referenz" value={referenz} onChange={e => setReferenz(e.target.value)} /></label>
                )}
              </div>
              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <table className="w-full text-[13px]" data-testid="liste-vorschau">
                  <thead className="bg-slate-100 text-xs text-slate-600 uppercase">
                    <tr>{['Pos.', 'Stück', 'Was', 'Wunschlieferant', ''].map(h => <th key={h} className="text-left px-2.5 py-1.5 font-medium">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {zeilen.map((z, i) => (
                      <tr key={i} className={z.was.trim() ? '' : 'bg-amber-50'}>
                        <td className="px-2.5 py-1.5 text-slate-400">{(i + 1) * 10}</td>
                        <td className="px-1.5 py-1"><input className="input w-28" aria-label={`Zeile ${i + 1} Stück`} value={z.stueck} onChange={e => set(i, { stueck: e.target.value })} /></td>
                        <td className="px-1.5 py-1"><input className="input w-full min-w-[18rem]" aria-label={`Zeile ${i + 1} Was`} value={z.was} onChange={e => set(i, { was: e.target.value })} /></td>
                        <td className="px-1.5 py-1"><input className="input w-40" list="sl-lieferanten" aria-label={`Zeile ${i + 1} Lieferant`} value={z.lieferant} onChange={e => set(i, { lieferant: e.target.value })} /></td>
                        <td className="px-1.5 py-1"><button aria-label={`Zeile ${i + 1} entfernen`} className="text-slate-300 hover:text-red-600 text-lg px-1"
                                                          onClick={() => setZeilen(l => (l ?? []).filter((_, k) => k !== i))}>×</button></td>
                      </tr>
                    ))}
                    {zeilen.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-500">Keine Zeilen erkannt.</td></tr>}
                  </tbody>
                </table>
              </div>
              {kategorie === 'schweissen' && <div className="text-xs text-slate-500">Anfrage- und Bestelltexte enthalten automatisch: „{ZEUGNIS_HINWEIS}“</div>}
              {fehler && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{fehler}</div>}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button className="btn-secondary" disabled={busy} onClick={() => setZeilen(null)}>← Zurück</button>
              <button data-testid="liste-anlegen" disabled={busy || gueltig.length === 0} onClick={() => void anlegen()}
                      className="bg-brand text-white rounded-lg px-5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-40">
                {busy ? 'Legt an …' : `${gueltig.length} Einträge anlegen${lieferantenAnzahl ? ` (${lieferantenAnzahl} Lieferant${lieferantenAnzahl === 1 ? '' : 'en'})` : ''}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
